import React, { memo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Collapse,
  styled,
  Skeleton,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FreeDeliveryTag from "../../animations/FreeDeliveryTag";
import { useLocation } from "react-router";
import CouponSideNav from "./coupon/CouponSideNav";
import { ApplyCoupon } from "./coupon/ApplyCoupon";
import useApiQuery from "../../hooks/useApiQuery";
import { getActivePromo } from "../../utilities/service";
import { useCartContext } from "../../hooks/CartContext";
import { useAuth } from "../../hooks/AuthContext";

const CartCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderRadius: "16px",
  padding: "24px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,250,242,0.96) 100%)",
  paddingTop: "16px",
  boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
}));

const BillLabel = styled(Box)(({ theme }) => ({
  fontWeight: 400,
  fontSize: "16px",
  color: "var(--neutral-800)",
  textAlign: "left",
  lineHeight: "150%",
}));

const BillValue = styled(Box)(({ theme, type }) => ({
  fontWeight: 700,
  fontSize: "16px",
  color: type === "negative" ? "red" : "var(--neutral-black)",
  textAlign: "right",
  lineHeight: "150%",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0px 0px 8px 0px",
  borderBottom: "1px solid #e5e7eb",
  backgroundColor: "#fff",
}));

const BillingDetails = ({
  itemCount,
  deliveryType,
  deliveryFeeData,
  billDetails,
  promo,
  // setTotalBill,
  cartSubTotal,
  backupTotalBill,
  isLoading,
  fromOrderDetails = false,
  cartItems = [], // ✅ full list of cart items to detect combo-only cart
}) => {
  const location = useLocation();
  const { applyDiscount } = useCartContext();
  const { user } = useAuth();
  const [toggleDetails, setToggleDetails] = useState(true);
  const [couponDrawer, setCouponDrawer] = useState(false);

  // ✅ Detect combo-only cart HERE so both ApplyCoupon and CouponSideNav are controlled
  const hasNormalProducts = cartItems.some((item) => !item?.parent_id);
  const isComboOnly = cartItems.length > 0 && !hasNormalProducts;

  const { data: promoData, isLoading: promoLoader } = useApiQuery({
    queryKey: ["getActivePromo", user?.customer_id],
    queryFn: () => getActivePromo(user?.customer_id),
    enabled: !fromOrderDetails,
  });

  // ✅ Combine normal promos + best custom promo
  const promoList = React.useMemo(() => {
    const normal = promoData?.data?.promos || [];
    const custom = promoData?.data?.applied_promo
      ? [promoData.data.applied_promo]
      : [];
    return [...normal, ...custom];
  }, [promoData]);

  const handleCouponDrawer = (open) => (event) => {
    // ✅ allow manual call without event
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setCouponDrawer(open);
  };

  const discount = location.pathname.includes("my-orders")
    ? promo
    : applyDiscount;
  return (
    <>
      <Box
        sx={{ gap: 2, display: "flex", flexDirection: "column", width: "100%" }}
      >
        {promoLoader ? (
          <Skeleton
            variant="rectangular"
            width={"100%"}
            height={60}
            sx={{ borderRadius: 1 }}
          />
        ) : (
          promoList?.length > 0 &&
          !fromOrderDetails && (
            <ApplyCoupon
              open={couponDrawer}
              handleCouponDrawer={handleCouponDrawer}
              dynamicCouponSave={billDetails?.dynamicCouponSave}
            />
          )
        )}

        <CartCard sx={{ pb: 1 }}>
          <HeaderBox>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: {
                  sm: "16px",
                  md: "18px",
                  xs: "15px",
                },
                color: "var(--neutral-black)",
                textAlign: "left",
                display: "flex",
                gap: 2,
              }}
            >
              <ReceiptLongIcon
                sx={{
                  fontSize: 20,
                  color: "var(--primary-red-base)",
                  mt: 0.75,
                }}
              />
              Order Snapshot
            </Typography>
          </HeaderBox>

          {billDetails?.cartSubTotal > 0 && (
            <Row
              rowNo={1}
              isLoading={isLoading}
              label={`Items (${itemCount?.cart})`}
              value={billDetails?.cartSubTotal}
              fromOrderDetails={fromOrderDetails}
            />
          )}
          {/* ✅ ADD THIS - Product discount row */}
          {+billDetails?.productDiscountSave > 0 && (
            <Row
              type="negative"
              rowNo={2}
              isLoading={isLoading}
              label="Discount"
              value={Number(billDetails?.productDiscountSave).toFixed(2)}
              fromOrderDetails={fromOrderDetails}
            />
          )}
          {+billDetails?.dynamicCouponSave > 0 && (
            <Row
              type="negative"
              rowNo={2}
              isLoading={isLoading}
              label={`Coupon Discount${
                discount?.type === "PERCENTAGE"
                  ? ` ${discount?.percentage}%`
                  : ""
              }`}
              value={Number(billDetails?.dynamicCouponSave).toFixed(2)}
              color="red"
              fromOrderDetails={fromOrderDetails}
            />
          )}

          {billDetails?.bulkSubTotal > 0 && (
            <Row
              rowNo={3}
              isLoading={isLoading}
              label={`Bulk Products (${itemCount?.bulk})`}
              value={billDetails?.bulkSubTotal}
              fromOrderDetails={fromOrderDetails}
            />
          )}

          {itemCount?.addon > 0 && (
            <Row
              rowNo={4}
              isLoading={isLoading}
              label={`Packaging upgrades (${itemCount?.addon})`}
              value={billDetails?.addonTotalAmount}
              fromOrderDetails={fromOrderDetails}
            />
          )}

          {deliveryType === "DOOR_STEP_DELIVERY" &&
            deliveryFeeData?.rate > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: fromOrderDetails ? "14px" : "16px",
                      color: "var(--neutral-900)",
                      textAlign: "left",
                      lineHeight: "150%",
                    }}
                  >
                    Shipping fee
                  </Typography>
                  {+deliveryFeeData?.free_delivery === 1 && <FreeDeliveryTag />}
                </Box>
                {+deliveryFeeData?.free_delivery === 1 ? (
                  <>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: fromOrderDetails ? "14px" : "16px",
                        textAlign: "right",
                        lineHeight: "150%",
                        color: "red",
                      }}
                    >
                      ₹{" "}
                      {deliveryFeeData?.rate
                        ? deliveryFeeData?.rate?.toFixed(2)
                        : 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        textAlign: "right",
                        lineHeight: "150%",
                        textDecoration: "line-through",
                        color: "red",
                        fontSize: 14,
                      }}
                    >
                      ₹{" "}
                      {deliveryFeeData?.rate
                        ? deliveryFeeData.rate.toFixed(2)
                        : 0}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: fromOrderDetails ? "14px" : "16px",
                      textAlign: "right",
                      color: "var(--neutral-black)",
                      lineHeight: "150%",
                    }}
                  >
                    ₹{" "}
                    {deliveryFeeData?.rate
                      ? deliveryFeeData?.rate.toFixed(2)
                      : 0}
                  </Typography>
                )}
              </Box>
            )}

          {/* Toggle Section */}
          <Row
            rowNo={5}
            label="Taxable"
            value={billDetails?.taxableAmount}
            isLoading={isLoading}
            fromOrderDetails={fromOrderDetails}
          />
          <Row
            rowNo={6}
            label="GST"
            value={billDetails?.gstAmount}
            isLoading={isLoading}
            fromOrderDetails={fromOrderDetails}
          />
          {!isLoading && (
            <Collapse in={toggleDetails}>
              {billDetails?.gstSlab?.length > 0 &&
                billDetails?.gstSlab?.map((item, index) => (
                  <Box key={index} sx={{ ml: 2 }}>
                    <Row
                      label={`CGST (${item?.cgstPercent}%)`}
                      value={item?.cgst}
                      fontSize={12}
                    />
                    <Row
                      label={`SGST (${item?.sgstPercent}%)`}
                      value={item?.sgst}
                      fontSize={12}
                    />
                  </Box>
                ))}
            </Collapse>
          )}

          {/* Toggle Button */}
          <Button
            fullWidth
            variant="text"
            onClick={() => setToggleDetails((prev) => !prev)}
            sx={{ textTransform: "none", color: "grey", fontSize: 12 }}
          >
            {toggleDetails ? "Show less" : "Show more"}
            {toggleDetails ? <ExpandLess /> : <ExpandMore />}
          </Button>

          <Divider sx={{ my: 1, backgroundColor: "#E9E9E9" }} />

          <Row
            label="Payable now"
            value={Math.round(billDetails?.totalAmount)}
            rowNo={7}
            isLoading={isLoading}
            fromOrderDetails={fromOrderDetails}
          />
        </CartCard>
      </Box>

      {/* ✅ Do NOT render CouponSideNav at all for combo-only carts
          This prevents the useEffect inside from firing eligibility toasts on mount */}
      {promoList?.length > 0 && (
        <CouponSideNav
          open={couponDrawer}
          handleCouponDrawer={handleCouponDrawer}
          // ✅ normalProductSubTotal excludes combos from coupon min-value checks
          cartSubTotal={billDetails?.normalProductSubTotal ?? cartSubTotal}
          backupTotalBill={backupTotalBill}
          promoList={promoList} // ✅ already combined
          appliedPromo={promoData?.data?.applied_promo} // ✅ from original data
          cartItems={cartItems}
        />
      )}
    </>
  );
};

// ✅ Reusable Row Component
const Row = ({
  label,
  value,
  fontSize,
  isLoading,
  rowNo,
  type = "positive",
  fromOrderDetails = false,
}) => {
  const width = rowNo % 2 == 0 ? "120px" : "150px";
  const resolvedFontSize = fontSize
    ? fontSize
    : fromOrderDetails
      ? 14
      : undefined;

  return isLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        my: 2,
      }}
    >
      <Skeleton
        variant="rectangular"
        width={width}
        height={20}
        sx={{ borderRadius: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width={width}
        height={20}
        sx={{ borderRadius: 1 }}
      />
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        my: 1,
        mt: label == "Total" ? 2 : 1,
      }}
    >
      <BillLabel sx={{ fontSize: resolvedFontSize }}>{label}</BillLabel>
      <BillValue sx={{ fontSize: resolvedFontSize }} type={type}>
        {type == "negative" ? `- ₹ ${value}` : `₹ ${value}`}
      </BillValue>
    </Box>
  );
};

export default memo(BillingDetails);
